import React, { useEffect, useMemo, useState, useRef } from "react";
import css from "./style.scss";
import uparrow from "../../assets/images/uparrow.svg";
import downarrow from "../../assets/images/downarrow.svg";

// Same UI as AutocompleteDropdown but clears the filter every time it opens
const ResettableAutocompleteDropdown = ({
  items,
  label,
  placeholder,
  onSelect,
  required = false,
  value = null,
  noDataMessage = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(value);
  const [searchValue, setSearchValue] = useState("");
  const containerRef = useRef(null);
  const dropdownInputRef = useRef(null);

  // Keep selected value in sync with incoming prop
  useEffect(() => {
    if (value && value.name) {
      setSelectedItem(value);
    } else {
      setSelectedItem(null);
    }
  }, [value]);

  // Handle clicks outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use a small delay to avoid immediate closure when opening
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Always clear search when dropdown opens so the full list is shown
  const openDropdown = () => {
    setSearchValue("");
    setIsOpen(true);
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (!prev) {
        setSearchValue("");
        dropdownInputRef && dropdownInputRef.current && dropdownInputRef.current.focus();
      }
      return next;
    });
  };

  const filteredItems = useMemo(() => {
    const term = searchValue.trim().toLowerCase();

    if (!term) return items;

    return items.filter((item) =>
      item.name.toLowerCase().startsWith(term)
    );
  }, [searchValue, items]);

  const inputDisplayValue = isOpen ? searchValue : selectedItem?.name || "";

  return (
    <div className={css.autocompleteContainer} ref={containerRef}>
      <label className={css.autocompleteLabel}>
        {label} {required && <span className={css.requiredAsterisk}> *</span>}
      </label>

      <div
        className={`${css.autocompleteInputWrapper} ${isOpen ? css.open : ""}`}
      >
        <input
          type="text"
          ref={dropdownInputRef}
          value={inputDisplayValue}
          onChange={(e) => {
            setSearchValue(e.target.value);
            setIsOpen(true);
          }}
          onFocus={openDropdown}
          placeholder={placeholder}
          className={css.autocompleteInput}
        />

        <button
          className={css.dropdownArrow}
          onClick={toggleDropdown}
          type="button"
        >
          <img
            src={isOpen ? uparrow : downarrow}
            alt="arrow"
            className={css.arrowIcon}
          />
        </button>
      </div>

      {isOpen && (
        <ul className={css.autocompleteList}>
          {filteredItems.length > 0
            ? filteredItems.map((item) => (
              <li
                key={item.id}
                className={css.autocompleteItem}
                onMouseDown={() => {
                  setSelectedItem(item);
                  setIsOpen(false);
                  setSearchValue("");

                  if (onSelect) onSelect(item);
                }}
              >
                {item.name}
              </li>
            ))
            : noDataMessage && (
              <li className={css.noDataMessage}>{noDataMessage}</li>
            )}
        </ul>
      )}
    </div>
  );
};

export default ResettableAutocompleteDropdown;
